/**
 * recent-screens.helper.ts
 *
 * Pure functions for Recent Screens feature.
 * ─────────────────────────────────────────────
 * Rules:
 *  - No DI, no services, no side effects, no imports from Angular/RxJS.
 *  - All logic is deterministic: same input → same output.
 *  - Consumed exclusively by RecentScreensService.
 */

export interface RecentScreen {
  /** Route path, e.g. '/sales/sales-invoices/create-invoice/create/new' */
  link: string;
  titleAr: string;
  titleEn: string;
  /** Icon class key, e.g. 'ki-element-11' */
  icon: string;
  /** Unix ms timestamp of last visit — used for ordering only, not financial data */
  visitedAt: number;
  /** Optional: permission key required to CREATE this resource. Used for default create screens. */
  createPermissionKey?: string;
  /** Optional: permission key required to VIEW this resource list. Used for default screens. */
  viewPermissionKey?: string;
}

/**
 * Max number of recent screens stored per user.
 * Must match the largest visibleScreenCount value (xxl = 15) so that
 * the quick-access panel is always completely filled on large displays.
 */
export const MAX_RECENT_SCREENS = 15;

/**
 * Adds (or promotes) a screen to the front of the recent list.
 * - Deduplicates by `link` (case-insensitive).
 * - Moves existing entry to position 0 if it was already in the list.
 * - Limits total to MAX_RECENT_SCREENS.
 */
export function addToRecentScreens(
  current: RecentScreen[],
  incoming: RecentScreen
): RecentScreen[] {
  const normalizedLink = incoming.link.toLowerCase();

  // Remove existing entry with the same link (dedup)
  const filtered = current.filter(
    (s) => s.link.toLowerCase() !== normalizedLink
  );

  // Prepend with updated timestamp
  const updated: RecentScreen = { ...incoming, visitedAt: Date.now() };

  return [updated, ...filtered].slice(0, MAX_RECENT_SCREENS);
}

/**
 * Type guard: checks that a raw object has the required shape.
 * Used to validate items loaded from storage before trusting them.
 */
export function isValidRecentScreen(raw: unknown): raw is RecentScreen {
  if (!raw || typeof raw !== 'object') return false;
  const r = raw as Record<string, unknown>;
  return (
    typeof r['link'] === 'string' &&
    r['link'].trim().length > 0 &&
    typeof r['titleAr'] === 'string' &&
    typeof r['titleEn'] === 'string' &&
    typeof r['icon'] === 'string' &&
    typeof r['visitedAt'] === 'number'
  );
}

/**
 * Sanitizes a raw array from storage — filters invalid/corrupted entries.
 * Safe to call with any unknown value.
 */
export function sanitizeStoredScreens(raw: unknown): RecentScreen[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isValidRecentScreen).slice(0, MAX_RECENT_SCREENS);
}
